import { Injectable } from "@angular/core";
import { Firestore, doc, DocumentReference } from 'firebase/firestore';
import { docData } from 'rxfire/firestore';
import { BehaviorSubject, Observable, of, forkJoin } from "rxjs";
import {
  map,
  tap,
  shareReplay,
  catchError,
  filter
} from "rxjs/operators";
import { Modificator, Modificators } from "src/interfaces/modificators.model";
import { getAllergenEnumValue } from "src/interfaces/enums.model";
import { convertLanguagesMap } from "../utils/convert-language";

@Injectable({
  providedIn: 'root'
})

export class ModificatorsService {
  private modificatorsMap = new Map<string, Observable<Modificators>>(); // Кэшируем потоки
  private modificatorsSubject = new BehaviorSubject<Modificators[]>([]); // Глобальный список

  constructor(private store: Firestore) { }

  /**
   * Получение потока модификатора по ID. Создает подписку, если её ещё нет.
   */
  public modificators(id: string): Observable<Modificators> {
    if (!this.modificatorsMap.has(id)) {
      const modificator$ = this.fetchModificatorFromFirebase(id).pipe(
        filter((modificator): modificator is Modificators => modificator !== null),
        tap(modificator => {
          if (modificator) {
            // console.log('ModificatorsService | modificators()', modificator);
            this.updateCache(modificator);
          }
        }),
        shareReplay(1) // Кэш последнего значения
      );

      this.modificatorsMap.set(id, modificator$);
    }

    return this.modificatorsMap.get(id)!;
  }

  /**
   * Запрос документа из Firestore с подпиской на обновления.
   */
  private fetchModificatorFromFirebase(id: string): Observable<Modificators | null> {
    const modificatorDocRef: DocumentReference = doc(this.store, 'modificators', id);
    return docData(modificatorDocRef, { idField: 'id' }).pipe(
      map((rawData: any) => this.processModificator(rawData)),
      catchError(error => {
        console.error(`Error fetching modificator with id "${id}":`, error);
        return of(null);
      })
    );
  }

  /**
   * Обработка модификатора: установка значений по умолчанию.
   */
  private processModificator(modificator: any): Modificators {
    if (!modificator || typeof modificator !== 'object') return modificator;
  
    return {
      ...modificator,
      languages: convertLanguagesMap(modificator.languages),
      items: Array.isArray(modificator.items)
        ? modificator.items.map((item: Modificator) => ({
            ...item,
            count: item.count ?? 0,
            maximum: item.maximum ?? 1,
            // В Firestore — строки, как у маркеров блюд
            allergens: Array.isArray(item.allergens)
              ? (item.allergens as unknown as string[]).map(getAllergenEnumValue)
              : [],
            languages: convertLanguagesMap(item.languages),
          }))
        : [],
    };
  }

  /**
   * Обновление кеша BehaviorSubject.
   */
  private updateCache(modificator: Modificators): void {
    if (!modificator?.id) return;

    const current = this.modificatorsSubject.getValue().filter(Boolean); // убираем undefined
    const index = current.findIndex(m => m?.id === modificator.id); // безопасная проверка

    const updated = index === -1
      ? [...current, modificator]
      : [...current.slice(0, index), modificator, ...current.slice(index + 1)];

    this.modificatorsSubject.next(updated);
  }

  /**
   * Получение модификаторов по массиву ID из текущего кэша.
   */
  public getModificators(ids: string[]): Modificators[] {
    const all = this.modificatorsSubject.getValue();
    return all.filter(m => ids.includes(m.id));
  }

  /**
   * Получение модификаторов по массиву ID с подпиской на их изменения.
   */
  public getModificatorsAsync(ids: string[]): Observable<Modificators[]> {
    const observables = ids.map(id => this.modificators(id));
    return forkJoin(observables);
  }
}